import { RequestTypeCards } from '@/components/RequestTypeCard';
import {
  ServiceRequestContext,
  initialState,
  serviceRequestType,
} from '@/context/serviceRequestContext';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ProductContext } from '@/context/productContext';
import Div from '@/shared/Div';
import Paragraph from '@/shared/Paragraph';
import Button from '@/shared/Button';
import FixedWrapper from '@/shared/FixedWrapper';
import AppBar from '@/shared/AppBar';
import Header from '@/shared/Header';
import BackgroundBlue from '@/components/Icons/BackgroundBlue';
import SelectedDeviceCard from '@/components/AMC/SelectedDeviceCard';
import useScreen from '@/hooks/useScreen';
import useOnMount from '@/hooks/useOnMount';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import { useRouter } from 'next/router';
import { AMC_DEVICE_PAGE_URL } from '@/configs/constants';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import { fetchDispositions } from '@/actions/fetchDispositions';
import is from '@/utils/is';
import ContentLoader from 'react-content-loader';
import { DEVICE_STATUS } from '@/constants/app';
import AppDownloadCard from '@/components/Static/AppDownloadCard';

const RequestTypeLoader = (props: any) => (
  <ContentLoader
    speed={2}
    width={400}
    height={575}
    viewBox="0 0 400 575"
    backgroundColor="#F9F9F9"
    foregroundColor="#ecebeb"
    className="w-full"
  >
    <rect x="0" y="0" rx="12" ry="12" width="450" height="300" />
  </ContentLoader>
);

export default function RequestTypes() {
  const { dispatch: serviceAction, state: serviceState } = useContext(ServiceRequestContext);
  const { state: productState } = useContext(ProductContext);
  const { selectedProduct } = productState;
  const router = useRouter();
  const { serviceType } = serviceState;
  const { fireGTMEvent } = useGTM();
  const [localServiceType, setLocalServiceType] = useState(serviceType);
  const { isDesktop } = useScreen();
  const setServiceType = (value: serviceRequestType) => {
    serviceAction.updateServiceType(value);
    setLocalServiceType(value);
  };
  const [requestTypes, setRequestTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccessError, setHasAccessError] = useState(false);
  const fetch = (...args: any) => fetchDispositions.apply(null, args);
  const { promisifyTrigger } = useTriggerRequest({ fetch });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const serviceTypeVisible = useMemo(() => {
    let service_types = requestTypes.map((item: { name: string }) => item.name);

    if (selectedProduct.isRental) {
      service_types = service_types.filter((st) => st !== 'Re-Install' && st !== 'Uninstall');
    }

    return service_types.join(',');
  }, [selectedProduct, requestTypes]);

  useOnMount(() => {
    fireGTMEvent(GTMEventNames.SERVICE_TYPE_SELECTION_SCREEN_LANDED, {
      service_type_visible: serviceTypeVisible,
      screen_name: SCREEN_NAMES.SERVICE_TYPE_SELECTION,
    });
  });

  useEffect(() => {
    const setDispositions = async () => {
      try {
        const {
          deviceId,
          expiryStatus: { status, daysToExpire },
          isIrRequired,
          isRental,
          hubCode,
        } = selectedProduct;
        let expiryCohort = status;
        if (isRental && status !== DEVICE_STATUS.IW && status !== DEVICE_STATUS.IC)
          expiryCohort = DEVICE_STATUS.IC;
        const { data } = await promisifyTrigger(
          deviceId,
          expiryCohort,
          hubCode,
          isIrRequired,
          daysToExpire,
        );
        setRequestTypes(data);
        setHasAccessError(false);
      } catch (error: any) {
        if (error?.statusCode === 403) {
          setHasAccessError(true);
          fireGTMEvent(
            GTMEventNames.SERVICE_BOOKING_BLOCKED_FROM_WEB,
            {
              deviceId: selectedProduct.deviceId,
              screen_name: SCREEN_NAMES.SERVICE_TYPE_SELECTION,
            },
            {
              webengage: {
                fire: true,
                isDirect: true,
              },
            },
          );
        } else {
          console.error('Error fetching dispositions:', error);
        }
      }
    };
    setDispositions();
  }, [selectedProduct]);

  const handleContinueClick = () => {
    if (localServiceType !== serviceType) {
      setIsLoading(true);
      serviceAction.updateUseCurrentAddress(false);
      serviceAction.updateAddress(initialState.address);
      serviceAction.updateRepairType({ defectType: '', addnRemarks: '' });
      setIsLoading(false);
    }

    serviceAction.updateServiceDate(new Date());
    serviceAction.updateServiceType(localServiceType);
    switch (localServiceType?.toLowerCase()) {
      case 'reinstall':
        serviceAction.changeComponent('reInstallForm');
        break;
      default:
        serviceAction.changeComponent('bookASlot');
        break;
    }

    fireGTMEvent(GTMEventNames.SERVICE_ISSUE_TYPE_CONTINUE_CLICKED, {
      service_type_visible: serviceTypeVisible,
      screen_name: SCREEN_NAMES.SERVICE_TYPE_SELECTION,
      issue_type_selected: serviceState?.defectType,
    });
  };

  const backButtonHandler = () => {
    router.push({
      pathname: AMC_DEVICE_PAGE_URL,
      query: {
        assetid: selectedProduct?.id,
      },
    });
    fireGTMEvent(GTMEventNames.SERVICE_SCREEN_BACK_CLICKED, {
      service_type_visible: serviceTypeVisible,
      screen_name: SCREEN_NAMES.SERVICE_TYPE_SELECTION,
      issue_type_selected: serviceState?.defectType,
    });
  };

  if (hasAccessError) {
    return (
      <AppDownloadCard
        title="Download Our Mobile App"
        subtitle="For booking service requests, please download the Eureka Forbes mobile app"
        playStoreUrl="https://play.google.com/store/apps/details?id=com.eurekaforbes.consumer"
        appStoreUrl="https://apps.apple.com/in/app/eureka-forbes/id1443884178"
        image={{
          data: {
            attributes: {
              hash: 'image_41911_8a2caf4612',
              ext: '.jpg',
              mime: 'image/jpeg',
              url: 'https://www.eurekaforbes.com/cms/assets/prod/image_41911_8a2caf4612.jpg',
            },
          },
        }}
      />
    );
  }

  return (
    <>
      <BackgroundBlue className="fixed max-h-screen left-0 top-0 overflow-hidden hidden lg:block" />
      <BackgroundBlue className="fixed max-h-screen right-0 top-0 overflow-hidden hidden lg:block rotate-180" />
      <Div className="-mt-8 relative">
        <AppBar
          transparent
          fixed
          onClick={backButtonHandler}
          onScrollLabel="Tell us the issue"
          preventScrollBackground={isDesktop}
        />
        <Div className="w-full max-w-screen-sm mx-auto">
          <Div className="flex flex-col gap-s4 mb-10 pt-16 w-80">
            <Header className="!text-h-1 md:text-hd-1 text-black/70">Tell us the issue</Header>
            <Paragraph className="text-body-lg text-black/50">
              A network of 8500+ certified Service Technicians are trained to solve your problems.
            </Paragraph>
          </Div>
          <Div className="flex flex-col gap-s6 justify-center">
            {is.empty.array(requestTypes) ? (
              <RequestTypeLoader />
            ) : (
              <RequestTypeCards
                serviceTypeVisible={serviceTypeVisible}
                data={requestTypes}
                setServiceType={setServiceType}
                localServiceType={localServiceType}
              />
            )}

            <FixedWrapper className="p-6 pt-4 md:pb-4 bg-white shadow-subtle ">
              <Div className="flex gap-s5 flex-col max-w-screen-sm mx-auto ">
                <SelectedDeviceCard className="md:border md:p-4 md:pb-1 md:rounded-cm" />
                <Button
                  dataTestId="request-type-next-button"
                  theme="primary"
                  className="w-full"
                  disabled={serviceState.defectType === '' ? true : false}
                  isLoading={isLoading}
                  onClick={handleContinueClick}
                >
                  Continue
                </Button>
              </Div>
            </FixedWrapper>
          </Div>
        </Div>
      </Div>
    </>
  );
}
